import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Watchlist, BenchmarkPortfolio, PortfolioType } from '../../monitoring.model';
import { MaterialModule } from 'src/Material-Module';
import { WatchlistStore } from '../../store/watchlist.store';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-watchlist-sidetab',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './watchlist-sidetab.component.html',
  styleUrl: './watchlist-sidetab.component.css',
})
export class WatchlistSidetabComponent implements OnInit, OnDestroy {
  @Input() watchlists: Watchlist[] = [];
  @Input() benchmarks: BenchmarkPortfolio[] = [];
  @Input() isLoading = false;
  @Output() sidebarCollapsed = new EventEmitter<boolean>();
  @Output() watchlistSelected = new EventEmitter<Watchlist>();
  @Output() benchmarkSelected = new EventEmitter<BenchmarkPortfolio>();
  @Output() createWatchlist = new EventEmitter<void>();
  @Output() editWatchlist = new EventEmitter<Watchlist>();
  @Output() deleteWatchlist = new EventEmitter<Watchlist>();
  @Output() sectionChanged = new EventEmitter<PortfolioType>();
  
  selectedWatchlist: Watchlist | null = null;
  selectedBenchmark: BenchmarkPortfolio | null = null;
  activeSection: PortfolioType = 'watchlist';
  private subscriptions: any[] = [];
  
  constructor(public watchlistStore: WatchlistStore, public dialog: MatDialog) {}
  
  ngOnInit(): void {
    // Subscribe to store changes
    this.subscriptions.push(
      this.watchlistStore.activeWatchlist$.subscribe(watchlist => {
        this.selectedWatchlist = watchlist;
      })
    );
    this.subscriptions.push(
      this.watchlistStore.activeBenchmark$.subscribe(benchmark => {
        this.selectedBenchmark = benchmark;
      })
    );
    this.subscriptions.push(
      this.watchlistStore.activeSection$.subscribe(section => {
        this.activeSection = section;
      })
    );
  }
  
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => {
      if (sub) {
        sub.unsubscribe();
      }
    });
  }

  isSidebarCollapsed = false;
  toggleSidebar() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    this.sidebarCollapsed.emit(this.isSidebarCollapsed);
  }

  onSectionToggle(section: PortfolioType) {
    if (this.activeSection === section) {
      return;
    }
    this.watchlistStore.setActiveSection(section);
    this.sectionChanged.emit(section);
    
    // Auto-select first item in new section
    if (section === 'watchlist' && this.watchlists.length > 0) {
      this.watchlistStore.setActiveWatchlist(this.watchlists[0]);
    } else if (section === 'benchmark' && this.benchmarks.length > 0) {
      this.watchlistStore.setActiveBenchmark(this.benchmarks[0]);
    }
  }

  onWatchlistClick(watchlist: Watchlist) {
    // Block switching while any data is loading
    if (this.watchlistStore.isAnyWatchlistLoading()) {
      return;
    }
    
    // Don't switch if already selected
    if (this.selectedWatchlist?.watchlist_id === watchlist.watchlist_id) {
      return;
    }
    
    this.watchlistStore.setActiveWatchlist(watchlist);
  }

  onBenchmarkClick(benchmark: BenchmarkPortfolio) {
    // Block switching while any data is loading
    if (this.watchlistStore.isAnyWatchlistLoading()) {
      return;
    }
    
    // Don't switch if already selected
    if (this.selectedBenchmark?.benchmark_id === benchmark.benchmark_id) {
      return;
    }
    
    this.watchlistStore.setActiveBenchmark(benchmark);
  }

  onCreateWatchlist() {
    this.createWatchlist.emit();
  }

  onEditWatchlist(watchlist: Watchlist) {
    this.editWatchlist.emit(watchlist);
  }

  onDeleteWatchlist(watchlist: Watchlist) {
    //use the confirmation dialog
    ConfirmationDialogComponent.confirmDelete(this.dialog, watchlist.watchlist_name).subscribe(result => {
      if (result) {
        this.deleteWatchlist.emit(watchlist);
      }
    });
  }
}
